const text = value => String(value ?? '').trim();
const normalized = value => text(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ');
const fields = ['street', 'number', 'neighborhood', 'city', 'state'];
const compatiblePlaceAddress = (place, current) => fields.every(field => !text(current[field])
  || (text(place.address?.[field]) && normalized(current[field]) === normalized(place.address[field])));

// Provenance belongs only to the private selection, never the address payload.
export const checkoutPlaceAutoReferenceName = (place = {}, current = {}) => {
  const name = text(place.name);
  const manual = compatiblePlaceAddress(place, current) ? text(current.reference) : '';
  return name && normalized(manual) !== normalized(name) ? name : '';
};

export const stripCheckoutPlaceAutoReference = (reference = '', autoName = '') => {
  const prefix = text(autoName); const value = String(reference ?? '');
  if (!prefix) return reference;
  if (value === prefix) return '';
  if (value.startsWith(`${prefix} — `)) return value.slice(prefix.length + 3);
  return reference;
};

// Selecting a different place replaces the geographic draft. Unknown fields
// cannot inherit the previous customer's address or house number.
export const checkoutPlaceAddress = (place = {}, current = {}) => {
  const source = place.address || {};
  const address = Object.fromEntries(fields.map(field => [field, text(source[field])]));
  if (!address.number) address.number = 'Não informado';
  address.state = address.state.toUpperCase();
  // Photon place postal codes are not independently corroborated. A DTO flag
  // or a previous address cannot turn them into verified postal evidence.
  address.cep = '';
  const compatible = compatiblePlaceAddress(place, current);
  address.reference = [text(place.name), compatible && text(current.reference)].filter(Boolean)
    .filter((value, index, values) => values.findIndex(other => normalized(other) === normalized(value)) === index).join(' — ');
  address.notes = compatible ? text(current.notes) : '';
  return address;
};

// A missing value may be completed; changing any known geographic value makes
// this observed point ineligible. The backend independently checks the token.
export const currentCheckoutPlaceToken = (selection, address = {}) => {
  if (!selection?.selectionToken || !selection.address) return '';
  return fields.every(field => !text(selection.address[field])
    || normalized(selection.address[field]) === normalized(address[field])) ? selection.selectionToken : '';
};

// UI guard only. The server authenticates the receipt and independently
// verifies this OSM identity before any quote/order can use a partial address.
export const isCheckoutPlaceSnapshot = snapshot => {
  const quality = snapshot?.quality;
  const place = quality?.place;
  const source = place?.address;
  if (snapshot?.status !== 'estimated' || quality?.level !== 'place-point' || quality?.provider !== 'photon'
    || !place || !source || !['N', 'W', 'R'].includes(place.osmType)
    || !/^\d{1,16}$/.test(place.osmId) || !Number.isSafeInteger(Number(place.osmId)) || Number(place.osmId) <= 0
    || quality.candidateId !== `photon:${place.osmType}:${place.osmId}`) return false;
  for (const [field, max] of Object.entries({ name: 255, osmKey: 80, osmValue: 80 })) {
    if (typeof place[field] !== 'string' || !text(place[field]) || text(place[field]).length > max) return false;
  }
  if (!['shop', 'amenity', 'office', 'leisure', 'tourism', 'craft', 'building', 'healthcare', 'club',
    'man_made', 'historic', 'aeroway', 'emergency', 'military'].includes(place.osmKey)) return false;
  for (const [field, max] of Object.entries({ street: 255, number: 50, neighborhood: 255, city: 255, state: 2, country: 2, postalCode: 8 })) {
    if (typeof source[field] !== 'string' || text(source[field]).length > max) return false;
  }
  return Boolean(text(source.city) && source.country === 'BR' && source.postalCode === ''
    && 'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'.split(' ').includes(source.state)
    && snapshot.address?.country === 'BR'
    && [...fields, 'country'].every(field => !text(source[field])
      || normalized(source[field]) === normalized(snapshot.address?.[field])));
};
